export const NOTE_ATTACHMENT_LIMITS = {
  maxImages: 5,
  maxImageBytes: 10 * 1024 * 1024,
  maxVideoBytes: 200 * 1024 * 1024,
};

export interface AttachmentRejection {
  key: string;
  params?: Record<string, string | number>;
}

export interface AttachmentPickResult {
  accepted: File[];
  rejection: AttachmentRejection | null;
}

const bytesToMB = (bytes: number) => Math.round(bytes / 1024 / 1024);

const isVideo = (file: File) => file.type.startsWith('video/');

const mixedMediaRejection: AttachmentRejection = {
  key: 'noteCaptureAttachments.photoAndVideoUnsupported',
};

export function validateImagePicks(
  picked: File[],
  existing: File[]
): AttachmentPickResult {
  if (existing.some(isVideo)) {
    return { accepted: [], rejection: mixedMediaRejection };
  }
  const oversized = picked.some(
    (f) => f.size > NOTE_ATTACHMENT_LIMITS.maxImageBytes
  );
  if (oversized) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.imageTooLarge',
        params: { maxMB: bytesToMB(NOTE_ATTACHMENT_LIMITS.maxImageBytes) },
      },
    };
  }
  const existingImages = existing.filter((f) => !isVideo(f));
  if (
    existingImages.length + picked.length >
    NOTE_ATTACHMENT_LIMITS.maxImages
  ) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.tooManyImages',
        params: { max: NOTE_ATTACHMENT_LIMITS.maxImages },
      },
    };
  }
  return { accepted: picked, rejection: null };
}

export function validateVideoPick(
  picked: File,
  existing: File[] = []
): AttachmentPickResult {
  if (existing.some((f) => !isVideo(f))) {
    return { accepted: [], rejection: mixedMediaRejection };
  }
  if (picked.size > NOTE_ATTACHMENT_LIMITS.maxVideoBytes) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.videoTooLarge',
        params: { maxMB: bytesToMB(NOTE_ATTACHMENT_LIMITS.maxVideoBytes) },
      },
    };
  }
  return { accepted: [picked], rejection: null };
}

export function validateMixedPicks(
  picked: File[],
  existing: File[]
): AttachmentPickResult {
  const video = picked.find(isVideo);
  if (video) return validateVideoPick(video, existing);
  return validateImagePicks(picked, existing);
}
